import { Action } from 'redux';

import { FinalApplicationStatus } from './qualification';
import { selectBusinessInfo } from '../features/application/reducers/business-info';
import { RootState } from '../store';
import {
  approveApplication,
  rejectInactiveApplication,
  rejectIneligibleApplication,
  rejectInvalidApplication,
  rejectLockedApplication,
  rejectUnsupportedApplication,
} from '../store/reducers/application-result';
import { verifyApplication } from '../store/reducers/application-result/applicantResult';

const invalidGenericDetail =
  "We can't approve your application for a Coast card at this time. \n" +
  'Look for an email from us with additional details.';

const applicationFinalStatusToAction: Record<FinalApplicationStatus, (state: RootState) => Action> = {
  denied_duplicate: () =>
    rejectInvalidApplication("We can't process your application because you already have a Coast account."),
  denied_too_young: () =>
    rejectIneligibleApplication('You must be at least 18 years old \nin order to apply for credit.'),
  denied_recent: () => rejectInvalidApplication('You have started another application within the last 6 months.'),
  denied_business_type: (state: RootState) =>
    rejectIneligibleApplication(
      `Sorry, we can not support ${selectBusinessInfo(state).businessInfo.businessType}s at this time.`,
    ),
  denied_revenue_too_low: () =>
    rejectIneligibleApplication(
      'We are currently unable to offer the Coast card to \n' +
        'businesses in your revenue category. \n\n' +
        'Look for an email from us with additional details.',
    ),
  denied_consumer_credit_lock: () =>
    rejectLockedApplication(
      'We are unable to proceed with your application because \n' +
        'there is a lock or freeze on your credit file. \n\n' +
        'To unlock your file, you can call Transunion at 800-916-8800 \n' +
        'and choose option 4 to speak with a representative. \n' +
        'Alternately, you can unlock your file online at www.transunion.com.\n\n' +
        'Remember, applying for a Coast account will not affect \n' +
        'your personal credit.',
    ),
  onboarded: () => approveApplication(),
  verified: () => verifyApplication(),

  denied_consumer_credit_knockout: () => rejectInvalidApplication(invalidGenericDetail),
  denied_commercial_credit_knockout: () => rejectInvalidApplication(invalidGenericDetail),
  denied_commercial_thin_knockout: () => rejectInvalidApplication(invalidGenericDetail),
  denied_thin_files_knockout: () => rejectInvalidApplication(invalidGenericDetail),
  denied_thin_high_revenue_knockout: () => rejectInvalidApplication(invalidGenericDetail),
  denied_thin_low_revenue_knockout: () => rejectInvalidApplication(invalidGenericDetail),
  denied_unable_to_verify_id: () => rejectInvalidApplication(invalidGenericDetail),
  denied_state_credit_line_knockout: () => rejectInvalidApplication(invalidGenericDetail),
  denied_manual_review: () => rejectInvalidApplication(invalidGenericDetail),
  denied_product_not_offered: () =>
    rejectUnsupportedApplication(
      "We currently don't offer the Coast card in the state \n" +
        'where your business is located, but please check back \n' +
        'later to see if Coast becomes available.',
    ),
  denied: () => rejectInvalidApplication(invalidGenericDetail),
  denied_policy_no_compensating_factor: () => rejectInvalidApplication(invalidGenericDetail),
  denied_other: () => rejectInvalidApplication(invalidGenericDetail),
  denied_consumer_credit_knockout_manual: () => rejectInvalidApplication(invalidGenericDetail),
  denied_commercial_credit_knockout_manual: () => rejectInvalidApplication(invalidGenericDetail),
  denied_inactive_30_days: () =>
    rejectInactiveApplication(
      'We have automatically declined your application \nafter 30 days of inactivity. \n\nLook for an email from us with additional details.',
    ),
  denied_verification: () =>
    rejectInvalidApplication("We can't process your application because you already have a Coast account."),
};

export const decodeApplicationFinalStatus = (
  status: FinalApplicationStatus | undefined,
  state: RootState,
): Action[] => {
  const actionGenerator = status ? applicationFinalStatusToAction[status] : null;
  if (actionGenerator) {
    return [actionGenerator(state)];
  } else {
    return [];
  }
};
